import type { Ref } from 'vue';
import { toRefs, watch } from 'vue';
import type { HekateDisplayForm } from '@onshape/www-inertia';
import { CAD_PLAN_ID, FieldHtmlNameUtil, L1Validations, Trans } from '@onshape/www-inertia';
import ServiceEndpointsUtil from '../../../../util/ServiceEndpointsUtil';
import ExternalUtil from '../../../../ExternalUtil';
import AntiSpamUtil from '../../../../util/AntiSpamUtil';
import CommonPreFilledProgressiveProfile from './CommonPreFilledProgressiveProfileDefinition';
import UnifiedSignupAgent from '../../coordinator/UnifiedSignup/UnifiedSignupAgent';
import { isSignupTestEnabled } from '~/hekate-www/external/util/E2eTestingUtil';

type ValidationResult = {
  isValid: boolean;
  errorMessage?: string;
};

type EmailValidation = {
  email: string;
  validResult: Promise<ValidationResult>;
};

export default class ReferToDiscoProgressiveProfile extends CommonPreFilledProgressiveProfile {
  private cachedEmailValidations: EmailValidation[] = [];

  /** Check the refCode and validate the email */
  public override realtimeBranchModifications({
    formDefinition,
    formNotValidErrorMsg,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
    formNotValidErrorMsg: Ref<string>;
  }): void {
    super.realtimeBranchModifications({ formDefinition });
    formDefinition.value.isRecaptcha = !isSignupTestEnabled();
    const container = document.querySelector(`div[form-id="${formDefinition.value.id}"]`);
    container?.classList.add('formfort-loading');
    formDefinition.value.displayFields.forEach((field) => {
      field.componentName = 'hidden-field';
    });

    ExternalUtil.findDisplayFieldOrThrow({
      formDefinition,
      fieldName: FieldHtmlNameUtil.approveUser,
    }).val = 'yes';

    // change email label to Business Email
    const emailField = ExternalUtil.findDisplayFieldOrThrow({
      formDefinition,
      fieldName: FieldHtmlNameUtil.email,
    });
    emailField.label = Trans.byValue('Business Email');

    ReferToDiscoProgressiveProfile.checkRefCode({
      formNotValidErrorMsg,
      formDefinition,
    });

    this.validateEmail({
      formDefinition,
    });
  }

  private static async checkRefCode({
    formNotValidErrorMsg,
    formDefinition,
  }: {
    formNotValidErrorMsg: Ref<string>;
    formDefinition: Ref<HekateDisplayForm>;
  }): Promise<boolean> {
    const url = new URL(document.URL);
    const refCode = url.searchParams.get('refCode');
    const container = document.querySelector(`div[form-id="${formDefinition.value.id}"]`);
    if (!refCode) {
      ReferToDiscoProgressiveProfile.hideFormTitle();
      formNotValidErrorMsg.value = Trans.byKey('expiredRefCodeErrorMsg');
      container?.classList.remove('formfort-loading');
      return false;
    }

    const response = await ReferToDiscoProgressiveProfile.sendRequest({
      url: ServiceEndpointsUtil.refCodeCheckEndpoint,
      body: {
        refCode,
        refCodeType: 'ref2disco',
        isGenLink: false,
      },
    });
    const result = await response.json();
    container?.classList.remove('formfort-loading');

    if (!result.isValid) {
      ReferToDiscoProgressiveProfile.hideFormTitle();
      formNotValidErrorMsg.value = Trans.byKey('expiredRefCodeErrorMsg');
      return false;
    }

    formDefinition.value.displayFields.forEach((field) => {
      field.componentName = field.initialComponentName;
    });

    ExternalUtil.findDisplayFieldOrThrow({
      formDefinition,
      fieldName: FieldHtmlNameUtil.referralCode,
    }).val = refCode;

    ExternalUtil.findDisplayFieldOrThrow({
      formDefinition,
      fieldName: FieldHtmlNameUtil.planId,
    }).val = CAD_PLAN_ID.PRO_DISCOVERY as string;

    return true;
  }

  private checkEmail(email: string): EmailValidation {
    const validationResult = this.cachedEmailValidations.find((item) => item.email === email);
    if (validationResult) {
      return validationResult;
    }

    const emailValidation: EmailValidation = {
      email,
      validResult: ReferToDiscoProgressiveProfile.validEmailCode(email),
    };

    this.cachedEmailValidations.push(emailValidation);
    return emailValidation;
  }

  private async validateEmail({ formDefinition }: { formDefinition: Ref<HekateDisplayForm> }) {
    const emailField = ExternalUtil.findDisplayFieldOrThrow({
      formDefinition,
      fieldName: FieldHtmlNameUtil.email,
    });

    const submitField = ExternalUtil.findDisplayFieldOrThrow({
      formDefinition,
      fieldName: FieldHtmlNameUtil.submit,
    });
    let lastCheckedEmail = '';
    const fieldVal = toRefs(emailField).val;
    watch(fieldVal, async (email) => {
      const validateError = L1Validations.validateSingleField({
        field: emailField,
        index: 0,
      });
      if (validateError) {
        submitField.isDisabled = true;
        return;
      }
      const tempEmail = email.toString().toLowerCase();
      lastCheckedEmail = tempEmail;

      const validationResult = this.checkEmail(tempEmail);
      const result = await validationResult.validResult;
      if (lastCheckedEmail !== validationResult.email) {
        return;
      }

      if (result.isValid) {
        emailField.errorMessages = [];
        submitField.isDisabled = false;
      } else {
        submitField.isDisabled = true;
        emailField.errorMessages = [
          {
            message: result.errorMessage || '',
            hint: result.errorMessage || '',
            isPersisted: true,
          },
        ];
      }
    });
  }

  public static async validEmailCode(email: string): Promise<ValidationResult> {
    const result = await UnifiedSignupAgent.emailCheck({ email });
    const { emailCheckCode } = result;
    if (emailCheckCode !== 200) {
      return {
        isValid: false,
        errorMessage: Trans.byKey('uniqueEmailAddressErrorMsg'),
      };
    }

    return {
      isValid: true,
    };
  }

  private static async sendRequest(request: { url: string; body: object }) {
    const nonce = Math.floor(Math.random() * (1 << 20)).toString(16);
    const response = await fetch(`${request.url}?nonce=${nonce}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(await AntiSpamUtil.commonFetchHeaders()),
      },
      body: JSON.stringify({
        ...request.body,
        csrfToken: AntiSpamUtil.getCsrfTokenOrNull(),
        signedScore: AntiSpamUtil.getSignedScore(),
      }),
    });
    return response;
  }

  private static hideFormTitle() {
    const formTitle = document.getElementById('form-header');
    formTitle?.classList.add('d-none');
  }
}
